import React, { ComponentProps, ComponentType, forwardRef, ReactNode } from "react";
import Box, { BoxProps } from "../box/Box";
import LoadingSpinner from "../LoadingSpinner";
import { useTheme } from "../theme/ThemeProvider";
import { getSecureHref, getExternalProps } from "../anchor/Anchor";

export interface IButton {
  /**
   * The size of the button
   */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * If `true`, the button will show a spinner.
   */
  loading?: boolean;
  /**
   * The color scheme of the button.
   *
   * This should be one of the color keys in the theme that has `100` - `900` color values (e.g.`green`, `red`).
   */
  variantColor?: string;
  /**
   * The variant of the button style to use.
   */
  variant?: "outline" | "ghost" | "unstyled" | "link" | "solid" | "primary" | "secondary";
  /**
   * If `true`, the button will be styled in it's active state.
   */
  active?: boolean;
  /**
   * If `true`, the button will be disabled.
   */
  disabled?: boolean;
  /**
   * The label to show in the button when `loading` is true
   * If no text is passed, it only shows the spinner
   */
  loadingText?: string;
  /**
   * If `true`, the button will take up the full width of its container.
   */
  fullWidth?: boolean;
  /**
   * The html button type to use.
   */
  type?: "button" | "reset" | "submit";
  /**
   * The content of the button.
   */
  children: ReactNode;
  /**
   * If added, the button will show an icon before the button's label.
   * Use the icon key in `theme.iconPath`
   */
  leftIcon?: ComponentType;
  /**
   * If added, the button will show an icon after the button's label.
   * Use the icon key in `theme.iconPath`
   */
  rightIcon?: ComponentType;
  /**
   * The space between the button icon and label.
   * Use the styled-system tokens or add custom values as a string
   */
  iconSpacing?: BoxProps["margin"];
}

const ButtonBox = Box.withComponent("button");

export type IButtonProps = IButton & Omit<ComponentProps<typeof ButtonBox>, "size">;

export const Button = forwardRef<any, IButtonProps>(
  (
    {
      disabled,
      active,
      fullWidth,
      children,
      variantColor = "gray",
      leftIcon,
      rightIcon,
      variant = "solid",
      loading,
      loadingText,
      iconSpacing = 2,
      type = "button",
      size = "md",
      ...rest
    },
    ref,
  ) => {
    const buttonStyleProps = useButtonStyle({
      color: variantColor,
      variant,
      size,
    });
    const _isDisabled = disabled || loading;

    return (
      <ButtonBox
        disabled={_isDisabled}
        aria-disabled={_isDisabled}
        ref={ref}
        type={type}
        borderRadius="md"
        fontWeight="semibold"
        width={fullWidth ? "full" : undefined}
        data-active={active ? "true" : undefined}
        {...buttonStyleProps}
        {...rest}
      >
        <ButtonContent
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          loading={loading}
          loadingText={loadingText}
          iconSpacing={iconSpacing}
        >
          {children}
        </ButtonContent>
      </ButtonBox>
    );
  },
);

Button.displayName = "Button";

const AnchorBox = Box.withComponent("a");

export type IAnchorButtonProps = IButton &
  Omit<ComponentProps<typeof AnchorBox>, "size"> & {
    external?: "outside-govlaunch" | "outside-redirect" | "govlaunch";
  };

export const AnchorButton = forwardRef<any, IAnchorButtonProps>(
  (
    {
      disabled,
      active,
      fullWidth,
      children,
      variantColor = "gray",
      leftIcon,
      external,
      rightIcon,
      variant = "solid",
      loading,
      target,
      loadingText,
      iconSpacing = 2,
      href,
      type = "button",
      size = "md",
      ...rest
    },
    ref,
  ) => {
    if (target === "_blank") {
      console.warn('You should not use target directly, please use `external="outside-govlaunch"`');
    }

    const buttonStyleProps = useButtonStyle({
      color: variantColor,
      variant,
      size,
    });
    const _isDisabled = disabled || loading;

    return (
      <AnchorBox
        aria-disabled={_isDisabled}
        ref={ref}
        type={type}
        href={getSecureHref(href, external)}
        target={target}
        borderRadius="md"
        fontWeight="semibold"
        width={fullWidth ? "full" : undefined}
        data-active={active ? "true" : undefined}
        {...getExternalProps(external)}
        {...buttonStyleProps}
        {...rest}
      >
        <ButtonContent
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          loading={loading}
          loadingText={loadingText}
          iconSpacing={iconSpacing}
        >
          {children}
        </ButtonContent>
      </AnchorBox>
    );
  },
);

AnchorButton.displayName = "AnchorButton";

function ButtonContent({ children, leftIcon, rightIcon, loading, loadingText, iconSpacing = 2 }: IButtonProps) {
  const renderLoadingContent = loadingText || (
    <Box as="span" opacity={0}>
      {children}
    </Box>
  );

  return (
    <>
      {leftIcon && !loading && <ButtonIcon mr={iconSpacing} icon={leftIcon} />}
      {loading && (
        <LoadingSpinner
          position={loadingText ? "relative" : "absolute"}
          mr={loadingText ? iconSpacing : 0}
          color="currentColor"
          emptyColor="transparent"
          size="xs"
        />
      )}
      {loading === true ? renderLoadingContent : children}
      {rightIcon && !loading && <ButtonIcon mr={-1} ml={iconSpacing} icon={rightIcon} />}
    </>
  );
}

export default Button;

interface IButtonIcon {
  icon: ComponentType;
}

const ButtonIcon = ({ icon, ...props }: IButtonIcon & BoxProps) => {
  // @ts-ignore
  return <Box as={icon} data-custom-icon={true} focusable="false" aria-hidden="true" color="currentColor" {...props} />;
};

const grayGhostStyle: { [key: string]: BoxProps } = {
  light: {
    color: "inherit",
    _hover: {
      bg: "gray.100",
    },
    _active: {
      bg: "gray.200",
    },
  },
  dark: {
    color: "whiteAlpha.900",
    _hover: {
      bg: "whiteAlpha.200",
    },
    _active: {
      bg: "whiteAlpha.300",
    },
  },
};

const ghostVariantProps = ({ color }: { color: string }): BoxProps => {
  if (color === "gray") {
    return grayGhostStyle;
  }

  return {
    bg: "transparent",
    color: `${color}.600`,
    _hover: {
      bg: `${color}.50`,
    },
    _active: {
      bg: `${color}.100`,
    },
  };
};

const outlineVariantProps = (props: { color: string }): BoxProps => {
  const { color } = props;

  return {
    border: "1px",
    borderColor: color === "gray" ? "gray.200" : "current",
    ...ghostVariantProps(props),
  };
};

const primaryVariantProps = (props: { color: string }): BoxProps => {
  return {
    border: "1px",
    ...solidVariantProps(props),
    bg: "blue.600",
    borderColor: "blue.600",
    color: "white",
    _hover: {
      bg: "blue.900",
      borderColor: "blue.900",
    },
    _disabled: {
      bg: "gray.200",
      borderColor: "gray.200",
    },
  };
};

const secondaryVariantProps = (props: { color: string }): BoxProps => {
  return {
    ...outlineVariantProps(props),
    bg: "white",
    color: "gray.600",
    _hover: {
      bg: "blue.600",
      borderColor: "blue.600",
      color: "white",
    },
    _disabled: {
      bg: "gray.100",
      borderColor: "gray.100",
      color: "white",
    },
  };
};

const graySolidStyle: { [key: string]: BoxProps } = {
  light: {
    bg: "gray.100",
    _hover: {
      bg: "gray.200",
    },
    _active: {
      bg: "gray.300",
    },
  },
  dark: {
    bg: "whiteAlpha.200",
    _hover: {
      bg: "whiteAlpha.300",
    },
    _active: {
      bg: "whiteAlpha.400",
    },
  },
};

const solidVariantProps = ({ color }: { color: string }): BoxProps => {
  if (color === "gray") {
    return graySolidStyle;
  }

  return {
    bg: `${color}.600`,
    color: "white",
    _hover: {
      bg: `${color}.600`,
    },
    _active: {
      bg: `${color}.700`,
    },
  };
};

const linkVariantProps = ({ color }: { color: string }): BoxProps => {
  return {
    p: 0,
    height: "auto",
    lineHeight: "normal",
    color: `${color}.600`,
    _hover: {
      textDecoration: "underline",
    },
    _active: {
      color: `${color}.700`,
    },
  };
};

const disabledProps: BoxProps = {
  _disabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    boxShadow: "none",
  },
};

const sizes: { [key: string]: BoxProps } = {
  lg: {
    height: 12,
    minWidth: 12,
    fontSize: "lg",
    px: 6,
  },
  md: {
    height: 10,
    minWidth: 10,
    fontSize: "md",
    px: 4,
  },
  sm: {
    height: 8,
    minWidth: 8,
    fontSize: "sm",
    px: 3,
  },
  xs: {
    height: 6,
    minWidth: 6,
    fontSize: "xs",
    px: 2,
  },
};

const sizeProps = ({ size = "md" }: { size?: "xs" | "sm" | "md" | "lg" }) => sizes[size];

const focusProps: BoxProps = {
  _focus: {
    boxShadow: "outline",
  },
};

const unstyledStyle: BoxProps = {
  userSelect: "inherit",
  bg: "none",
  border: 0,
  color: "inherit",
  display: "inline",
  fontFamily: "inherit",
  lineHeight: "inherit",
  m: 0,
  p: 0,
  textAlign: "inherit",
};

const variantProps = (props: IStyleProps) => {
  switch (props.variant) {
    case "solid":
      return solidVariantProps(props);
    case "ghost":
      return ghostVariantProps(props);
    case "link":
      return linkVariantProps(props);
    case "outline":
      return outlineVariantProps(props);
    case "primary":
      return primaryVariantProps(props);
    case "secondary":
      return secondaryVariantProps(props);
    case "unstyled":
      return unstyledStyle;
    default:
      return {};
  }
};

const baseProps = {
  display: "inline-flex",
  appearance: "none",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 250ms",
  userSelect: "none",
  position: "relative",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  lineHeight: "1.2",
  outline: "none",
};

interface IStyleProps {
  color: string;
  variant: IButtonProps["variant"];
  size: IButtonProps["size"];
}

const useButtonStyle = (props: IStyleProps): { [key: string]: Omit<Partial<BoxProps>, "ref"> } => {
  const theme = useTheme();
  const _props = { ...props, theme };

  // @ts-ignore
  return {
    ...baseProps,
    ...sizeProps(_props),
    ...focusProps,
    ...disabledProps,
    ...variantProps(_props),
  };
};
